export function Business() {
  return (
    <>
      <h1>The Business</h1>

      <p>
        AI collapses the cost of delivery. Customers still value outcomes at
        legacy price points. The margin gap is enormous — and it grows as AI
        improves.
      </p>
      <p>
        TAIGA sells governed software outcomes — not seats, tokens, or
        hours. If TAIGA does not deliver value, customers do not pay. This
        is not consulting. It is software that delivers consulting-grade
        outcomes at software margins.
      </p>

      <hr />

      <h2>The Paradigm Shift</h2>
      <table>
        <thead>
          <tr>
            <th>Dimension</th>
            <th>Traditional Consulting</th>
            <th>AI Tools (Copilot, Cursor)</th>
            <th>TAIGA (OaAS)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Pricing</td>
            <td>Time &amp; materials</td>
            <td>Per-seat / per-token</td>
            <td>Per-outcome</td>
          </tr>
          <tr>
            <td>Incentive alignment</td>
            <td>Misaligned (slower = more revenue)</td>
            <td>Neutral (productivity tool)</td>
            <td>Aligned (pay for results)</td>
          </tr>
          <tr>
            <td>Gross margins</td>
            <td>20-30%</td>
            <td>60-80%</td>
            <td>80-90%</td>
          </tr>
          <tr>
            <td>Governance</td>
            <td>Manual, expensive</td>
            <td>None built-in</td>
            <td>Built-in, automated</td>
          </tr>
          <tr>
            <td>Accountability</td>
            <td>Contractual (often weak)</td>
            <td>None (tool, not service)</td>
            <td>Structural (no outcome = no payment)</td>
          </tr>
          <tr>
            <td>Delivery risk</td>
            <td>Client bears most risk</td>
            <td>Client bears all risk</td>
            <td>TAIGA bears delivery risk</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>What It Costs</h2>
      <table>
        <thead>
          <tr>
            <th>Deliverable</th>
            <th>TAIGA Price</th>
            <th>Traditional Cost</th>
            <th>Customer Savings</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>MVP delivery</td>
            <td>EUR 10-50k</td>
            <td>EUR 100k+</td>
            <td>50-90%</td>
          </tr>
          <tr>
            <td>Feature delivery</td>
            <td>EUR 2-8k</td>
            <td>EUR 15-30k</td>
            <td>70-85%</td>
          </tr>
          <tr>
            <td>Bug fix</td>
            <td>EUR 200-1k</td>
            <td>EUR 2-5k</td>
            <td>80-90%</td>
          </tr>
        </tbody>
      </table>

      <p>
        <strong>How pricing works:</strong>
      </p>
      <ol>
        <li>Customer defines business goals and success criteria.</li>
        <li>TAIGA translates goals into machine-readable specifications.</li>
        <li>Both parties agree on deliverables and price.</li>
        <li>AI agents execute within governance boundaries.</li>
        <li>Payment triggers on delivery against agreed criteria.</li>
      </ol>

      <p>
        The governance engine serves as the contract. No scope creep, no
        billing disputes.
      </p>

      <hr />

      <h2>Unit Economics</h2>
      <p>
        <strong>80% gross margin.</strong> COGS consists of LLM API costs
        (~10-12%), AWS infrastructure (~5-7%), and third-party services
        (~2-3%). LLM costs are declining 50%+ year-over-year. There is no
        human delivery cost in COGS.
      </p>

      <p>
        <strong>MVP project at EUR 25k midpoint:</strong>
      </p>

      <table>
        <thead>
          <tr>
            <th>Line item</th>
            <th>Amount</th>
            <th>% of Revenue</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Revenue</td>
            <td>EUR 25,000</td>
            <td>100%</td>
          </tr>
          <tr>
            <td>LLM API</td>
            <td>-EUR 2,500</td>
            <td>10%</td>
          </tr>
          <tr>
            <td>Compute</td>
            <td>-EUR 1,500</td>
            <td>6%</td>
          </tr>
          <tr>
            <td>Third-party</td>
            <td>-EUR 1,000</td>
            <td>4%</td>
          </tr>
          <tr>
            <td>
              <strong>Gross Profit</strong>
            </td>
            <td>
              <strong>EUR 20,000</strong>
            </td>
            <td>
              <strong>80%</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        Feature delivery at the EUR 5k midpoint runs at ~84% margin — the
        Policy Card library and project context already exist, reducing
        per-delivery cost.
      </p>

      <hr />

      <h2>How the Model Evolves</h2>
      <table>
        <thead>
          <tr>
            <th>Phase</th>
            <th>Model</th>
            <th>Objective</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Phase 1: Pilot Customers</td>
            <td>
              Fixed MRR. Hands-on delivery, founder-led. Compliance
              Foundation Layer as entry wedge — MCP-based policy checking
              plugs into existing dev tools in hours.
            </td>
            <td>
              Learn, build references, validate. Expand from compliance
              layer to full platform.
            </td>
          </tr>
          <tr>
            <td>Phase 2: Self-Service Platform</td>
            <td>
              SaaS tiered pricing with token-based usage and platform burn
              credits.
            </td>
            <td>Product-led growth alongside enterprise sales.</td>
          </tr>
          <tr>
            <td>Phase 3: Scale</td>
            <td>
              Full OaAS at scale. Partner and channel ecosystem. Global
              expansion. Custom deployments for regulated industries.
            </td>
            <td>Market leadership in governed AI delivery.</td>
          </tr>
        </tbody>
      </table>

      <p>
        <strong>Land-and-expand:</strong> additional use cases, teams, and
        complexity tiers within existing accounts drive majority of revenue
        growth. NRR target: &gt;130% (enterprise land-and-expand pattern).
        Target ARR per customer: EUR 80-200k/year.
      </p>

      <hr />

      <h2>Where We Sell</h2>
      <table>
        <thead>
          <tr>
            <th>Sector</th>
            <th>Why TAIGA</th>
            <th>Key Compliance</th>
            <th>Engagement Type</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Defense</td>
            <td>
              Highest governance requirements, sovereign AI mandates
            </td>
            <td>NESA, Defsec</td>
            <td>Direct engagement</td>
          </tr>
          <tr>
            <td>Healthcare</td>
            <td>Strict compliance, patient data governance</td>
            <td>GDPR, medical device regulations</td>
            <td>Enterprise pilots</td>
          </tr>
          <tr>
            <td>Finance</td>
            <td>Regulatory-compliant autonomous operations, audit trails</td>
            <td>SOC 2, financial regulations, DORA</td>
            <td>Partnership</td>
          </tr>
          <tr>
            <td>Manufacturing</td>
            <td>Industrial automation with safety-first governance</td>
            <td>ISO standards, supply chain compliance</td>
            <td>Use-case driven</td>
          </tr>
        </tbody>
      </table>

      <p>
        All four sectors are heavily regulated, all need governance-first
        AI, and all are blocked by the same problem: no platform exists
        that combines autonomous execution with enterprise-grade governance.
        Every sector has active budget for AI transformation but cannot
        adopt current tools safely.
      </p>

      <p>
        Regulated industries share a common set of requirements: compliance,
        auditability, accountability, human oversight, data governance, and
        risk management. TAIGA addresses them structurally, not as add-on
        features.
      </p>

      <hr />

      <h2>What Makes TAIGA Different</h2>
      <ol>
        <li>
          <strong>Governance is the feature, not a checkbox.</strong>{" "}
          Regulated companies can actually adopt AI development because
          governance is structural, not bolted on.
        </li>
        <li>
          <strong>Full lifecycle.</strong> Not just code generation —
          specification, delivery, monitoring, and compliance. Learn, Build,
          Run.
        </li>
        <li>
          <strong>Enterprise DNA.</strong> 80+ years building and selling
          enterprise software across the founding team. They know how
          regulated companies buy.
        </li>
        <li>
          <strong>EU-first.</strong> Data residency and compliance are
          foundational, not afterthoughts. Infrastructure-enforced, not
          policy-enforced.
        </li>
        <li>
          <strong>Outcome pricing.</strong> Customers pay for results, not
          seats or tokens. Unique in the market. Incentives fully aligned.
        </li>
        <li>
          <strong>Strategy-to-Code link.</strong> LFA connects business
          intent to agent action. No competitor has this governance
          framework for AI agent orchestration.
        </li>
      </ol>
    </>
  );
}
